import { Fragment, useState } from "react";
import Session from "supertokens-web-js/recipe/session";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import {
  ArrowRightStartOnRectangleIcon,
  CalculatorIcon,
  Cog6ToothIcon,
  DocumentChartBarIcon,
  DocumentCheckIcon,
  MinusIcon,
  PencilSquareIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { useUser } from "../hooks/useUser";
import { useInfo } from "../hooks/useData";
import { calculateAge } from "./Info/PersonInfo";
import { Client } from "src/types";
import config from "src/services/config";
import Modal from "./Modal";
import NotesEditor from "./NotesEditor";
import Tasks from "./Tasks";
import router from "src/router";
import ReportTemplateModal from "./ReportTemplate/ReportTemplateModal";
import useClient from "src/hooks/clientStore";
import Button from "./Inputs/Button";
import  { AddTaskForm } from "./AddTask";

const NavItem = ({
  name,
  active,
  link,
}: {
  name: string;
  active: boolean;
  link: string;
}) => {
  return (
    <Link to={link}>
      <div
        className={`cursor-pointer py-2 px-3 font-semibold rounded-md ${active ? "bg-main-orange text-white" : ""}`}
      >
        {name}
      </div>
    </Link>
  );
};

const Navbar = ({ active, client }: { active: string; client?: Client }) => {
  const navigate = router.navigate;
  const { user } = useUser();
  const { data, setField } = useInfo();
  const notesOpen = useClient((state) => state.notesOpen);
  const setNotesOpen = useClient((state) => state.setNotesOpen);

  const [tasksOpen, setTasksOpen] = useState(false);

  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [reportTemplateOpen, setReportTemplateOpen] = useState(false);
  // const notes = client?.notes || "";

  const assetSummaryVisible = [
    "nikola.vojkovic@live.com",
    "nikola.vojkovic@toptal.com",
    "taylor@ataroke.com",
    "mike@beausayfinancial.com",
    "nclark@envisionrs.com",
  ].includes(user?.info?.email as any);
  console.log("ASS", data.tasks);
  const overdueTasksCount = data.tasks?.filter(
    (task) =>
      !task.completed && task.dueDate && new Date(task.dueDate) < new Date(),
  ).length;

  return (
    <div className="max-w-[1480px] px-10 m-auto flex justify-between items-center h-[72px] sticky top-10 bg-white z-5000 ">
      <div className=" flex justify-between items-center h bg-white w-full flex-1">
        <div className="flex items-center justify-start w-full flex-1">
          <Link to="/clients">
            <div
              className={`flex items-center cursor-pointer ${user?.info?.logo ? "" : "min-w-[200px]"}`}
            >
              {user?.info ? (
                <img
                  src={
                    user?.info?.logo
                      ? `${config.API_URL}logo/?logo=${user.info.logo}`
                      : "/img/logo.png"
                  }
                  className="h-9 mr-2 "
                />
              ) : (
                <div className="w-9 h-9"></div>
              )}

              {user?.info ? (
                <div className="font-bold text-[20px] leading-5">
                  {user?.info?.logo ? "" : "Income Mapper"}
                </div>
              ) : (
                <div className="w-9 h-9"></div>
              )}
            </div>
          </Link>
          {active == "data" ||
            active == "calculator" ||
            active == "map" ||
            active == "asset-summary" ||
            active == "basic" ||
            active == "longevity" ||
            active == "spending" ? (
            <div className="ml-3 flex gap-0">
              <NavItem
                name="Scenarios"
                active={active == "map"}
                link={`/client/${data.id}/map`}
              />
              <NavItem
                name="Spending"
                active={active == "spending"}
                link={`/client/${data.id}/spending`}
              />
              <NavItem
                name="Longevity"
                active={active == "longevity"}
                link={`/client/${data.id}/longevity`}
              />

              {assetSummaryVisible && (
                <NavItem
                  name="Asset Summary"
                  active={active == "asset-summary"}
                  link={`/client/${data.id}/asset-summary/income-cash`}
                />
              )}
            </div>
          ) : (
            <div className="ml-3 flex gap-0">
              <NavItem
                name="Clients"
                active={active == "clients"}
                link={`/clients`}
              />
              <NavItem
                name="Tasks"
                active={active == "tasks"}
                link={`/tasks`}
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-10 items-center ">
        {window.location.href.includes("/client/") && (
          <div className="flex gap-10 items-center ">
            <Link to={`/client/${data?.id}/calculator`}>
              <CalculatorIcon className=" w-6 mr-[-20px] cursor-pointer" />
            </Link>
            <PencilSquareIcon
              className="text-black w-6 mr-[-20px] cursor-pointer"
              onClick={() => setNotesOpen(true)}
            />
            <div className="relative">
              <DocumentCheckIcon
                className=" w-6 mr-[-20px] cursor-pointer"
                onClick={() => setTasksOpen(true)}
              />
              {overdueTasksCount > 0 && (
                <div
                  className="absolute -top-2 right-[-25px] bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold"
                  onClick={() => setTasksOpen(true)}
                >
                  {overdueTasksCount > 99 ? "99+" : overdueTasksCount}
                </div>
              )}
            </div>
            <Modal
              isOpen={tasksOpen}
              onClose={() => {
                setTasksOpen(false);
              }}
            >
              {tasksOpen && (
                <Tasks
                  initialTasks={client?.tasks || []}
                  client={client as any}
                  open={tasksOpen}
                  setExternal={(t: any) => {
                    if (typeof t === "function") {
                      setField("tasks")(t(data.tasks));
                    } else {
                      setField("tasks")(t);
                    }
                  }}
                />
              )}
            </Modal>

      <Modal
        isOpen={notesOpen}
        onClose={() => {
          // updateData(client.id.toString(), { ...client, notes: content });
          setNotesOpen(false);
          setAddTaskOpen(false);
        }}
      >
        <div className="p-4 min-h-[500px]">
          <div className="flex justify-between mb-4">
            <h2 className="text-2xl font-semibold ">Client Notes</h2>
            <div className="">
              {!addTaskOpen && (
                <Button
                  type={addTaskOpen ? "secondary" : "primary"}
                  onClick={() => setAddTaskOpen(!addTaskOpen)}
                  className="flex items-center gap-2"
                >
                  {addTaskOpen ? (
                    <MinusIcon className="w-4 h-4" />
                  ) : (
                    <PlusIcon className="w-4 h-4" />
                  )}
                  {addTaskOpen ? "Cancel" : "Add Task"}
                </Button>
              )}
            </div>
          </div>

          <div className="flex gap-4 transition-all duration-300 ease-in-out">
            <div
              className={`transition-all duration-300 ease-in-out h-[490px] ${addTaskOpen ? "w-[800px] flex-shrink-0" : "w-[800px]"
                }`}
            >
              <NotesEditor
                initialContent={data.notes}
                setContent={(notes: string) => {
                  setField("notes")(notes);
                }}
              />
            </div>

            <div
              className={`transition-all duration-300 ease-in-out overflow-hidden ${addTaskOpen ? "w-[300px] opacity-100" : "w-0 opacity-0"
                }`}
            >
              {addTaskOpen && (
                <div className="bg-gray-50 p-4 rounded-lg h-fit">
                  <h3 className="text-lg font-semibold mb-4">Add New Task</h3>
                  <AddTaskForm
                    hideCancel
                    clients={[client] as any}
                    defaultClientId={client?.id as any}
                    onTaskCreated={(task) => {
                      setField("tasks")([...data.tasks, task],
                      );
                      setAddTaskOpen(false);
                    }}
                    onCancel={() => setAddTaskOpen(false)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

            <Link to={`/client/${data.id}/basic`}>
              <div className="font-semibold text-[16px] ml-3 max-w-96 text-right text-main-orange cursor-pointer">
                {client?.people ? ` ${client?.title} ` : ""}{" "}
                <div className="font-normal text-gray-500 text-sm">
                  {client?.people
                    ?.map(
                      (item) =>
                        `${item.name} (${calculateAge(new Date(item.birthday))})`,
                    )
                    .join(" | ")}
                </div>
              </div>
            </Link>
          </div>
        )}

        <ReportTemplateModal
          open={reportTemplateOpen}
          setOpen={setReportTemplateOpen}
        />
        <Menu as="div" className="relative inline-block text-left z-5000">
          <div>
            <MenuButton className="w-10 h-10 rounded-full bg-[#F2F4F7] flex items-center justify-center cursor-pointer">
              <UserIcon className="h-6 text-[#667085]" />
            </MenuButton>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems className="absolute block right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-50000">
              <div className="px-1 py-1 z-50000">
                <MenuItem>
                  {({ active }) => (
                    <button
                      className={`${active ? "bg-main-orange text-white" : "text-gray-900"
                        } cursor-pointer group flex gap-2 w-full items-center rounded-md px-2 py-2 `}
                      onClick={() => setReportTemplateOpen(true)}
                    >
                      <div>
                        <DocumentChartBarIcon className="w-6 h-6" />
                      </div>
                      Report Templates
                    </button>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ active }) => (
                    <Link to="/profile">
                      <button
                        className={`${active ? "bg-main-orange text-white" : "text-gray-900"
                          } group flex gap-2 w-full items-center rounded-md px-2 py-2 `}
                      >
                        <div>
                          <Cog6ToothIcon className="w-6 h-6" />
                        </div>
                        Settings
                      </button>
                    </Link>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ active }) => (
                    <Link
                      to="https://intercom.help/income-mapper/en/"
                      target="_blank"
                    >
                      <button
                        className={`${active ? "bg-main-orange text-white" : "text-gray-800"
                          } hover:text-white group flex w-full gap-2 items-center rounded-md px-2 py-2 `}
                      >
                        <div>
                          <QuestionMarkCircleIcon className="w-6 h-6" />
                        </div>
                        <div className="">Help Center</div>
                      </button>
                    </Link>
                  )}
                </MenuItem>
              </div>
              <div className="px-1 py-1">
                <MenuItem>
                  {({ active }) => (
                    <button
                      className={`${active ? "bg-main-orange text-white" : "text-gray-900"
                        } group flex gap-2 w-full items-center rounded-md px-2 py-2 `}
                      onClick={async () => {
                        await Session.signOut();
                        navigate("/login");
                      }}
                    >
                      <div>
                        <ArrowRightStartOnRectangleIcon className="w-6 h-6" />
                      </div>
                      Log out
                    </button>
                  )}
                </MenuItem>
              </div>
            </MenuItems>
          </Transition>
        </Menu>
      </div>
    </div>
  );
};
export default Navbar;
